// Client-side audio compression for uncompressed formats (WAV, FLAC, AIFF).
// Decodes via Web Audio API, re-encodes to Opus/WebM via MediaRecorder.
// Leaves already-compressed formats (MP3, AAC, OGG) untouched.

export interface CompressAudioOptions {
  audioBitsPerSecond?: number    // encoder target bitrate (default 128 kbps)
  onProgress?: (frac: number) => void
  signal?: AbortSignal
}

const UNCOMPRESSED_TYPES = [
  'audio/wav',
  'audio/x-wav',
  'audio/wave',
  'audio/flac',
  'audio/x-flac',
  'audio/aiff',
  'audio/x-aiff',
]

export function isUncompressedAudio(file: File): boolean {
  return UNCOMPRESSED_TYPES.includes(file.type)
}

function pickAudioMimeType(): string | null {
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
  ]
  for (const m of candidates) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(m)) return m
  }
  return null
}

export function isAudioCompressionSupported(): boolean {
  return typeof MediaRecorder !== 'undefined' && pickAudioMimeType() !== null
}

export async function compressAudio(
  file: File,
  opts: CompressAudioOptions = {}
): Promise<File> {
  const audioBitrate = opts.audioBitsPerSecond ?? 128_000
  const onProgress = opts.onProgress
  const signal = opts.signal

  const mimeType = pickAudioMimeType()
  if (!mimeType) throw new Error('Browser does not support audio recording')

  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError')

  const arrayBuffer = await file.arrayBuffer()
  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError')

  const audioCtx = new AudioContext()
  const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer)
  await audioCtx.close()

  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError')

  // Create an offline context to render the audio, then pipe through MediaRecorder
  // via a MediaStreamAudioDestinationNode on a real-time context.
  const ctx = new AudioContext()
  const source = ctx.createBufferSource()
  source.buffer = audioBuffer
  const dest = ctx.createMediaStreamDestination()
  source.connect(dest)

  const recorder = new MediaRecorder(dest.stream, {
    mimeType,
    audioBitsPerSecond: audioBitrate,
  })
  const chunks: Blob[] = []
  recorder.ondataavailable = (e) => {
    if (e.data && e.data.size > 0) chunks.push(e.data)
  }

  const done = new Promise<void>((resolve, reject) => {
    recorder.onstop = () => resolve()
    recorder.onerror = (e: Event) => {
      const err = (e as unknown as { error?: Error }).error
      reject(err instanceof Error ? err : new Error('Recorder error'))
    }
  })

  let aborted = false
  const onAbort = () => {
    aborted = true
    try { source.stop() } catch { /* noop */ }
    if (recorder.state !== 'inactive') { try { recorder.stop() } catch { /* noop */ } }
  }
  if (signal) {
    signal.addEventListener('abort', onAbort)
  }

  // Progress via polling currentTime
  let progressInterval: ReturnType<typeof setInterval> | null = null
  if (onProgress) {
    progressInterval = setInterval(() => {
      if (audioBuffer.duration > 0) {
        onProgress(Math.min(1, ctx.currentTime / audioBuffer.duration))
      }
    }, 200)
  }

  source.onended = () => {
    if (recorder.state !== 'inactive') { try { recorder.stop() } catch { /* noop */ } }
  }

  recorder.start(1000)
  source.start()

  await done

  if (progressInterval) clearInterval(progressInterval)
  if (signal) signal.removeEventListener('abort', onAbort)
  await ctx.close()

  if (aborted) throw new DOMException('Aborted', 'AbortError')

  const ext = mimeType.includes('ogg') ? 'ogg' : 'webm'
  const blob = new Blob(chunks, { type: mimeType })
  const baseName = file.name.replace(/\.[^.]+$/, '')
  const outFile = new File([blob], `${baseName}.${ext}`, {
    type: mimeType,
    lastModified: Date.now(),
  })
  onProgress?.(1)
  return outFile
}
